import React, { useState, useEffect } from 'react';
import { Shield, Users, Search, Edit2, Save, X, Crown, UserCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getFullUrl } from '../modules/media/urlUtils';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  username: string;
  full_name: string;
  email?: string;
  user_type: 'artist' | 'visitor' | 'moderator' | 'admin';
  is_premium: boolean;
  avatar_url?: string;
  location?: string;
  created_at: string;
  account_status?: string;
  suspension_reason?: string;
  suspended_until?: string;
}

const UserManagementView: React.FC = () => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'artist' | 'visitor' | 'moderator' | 'admin'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'warned' | 'suspended' | 'banned'>('all');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState<'artist' | 'visitor' | 'moderator' | 'admin'>('visitor');
  const [newStatus, setNewStatus] = useState<'active' | 'warned' | 'suspended' | 'banned'>('active');
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionDays, setSuspensionDays] = useState(7);
  const [saving, setSaving] = useState(false);
  // Check if user has admin permissions
  const isAdmin = user?.user_type === 'admin';
  const isModerator = user?.user_type === 'moderator' || user?.user_type === 'admin';

  const [sendingPush, setSendingPush] = useState<string | null>(null);

  const sendTestPush = async (userId: string, username: string) => {
    if (!confirm(select({
      es: `¿Enviar notificación de prueba a ${username}?`,
      en: `Send test notification to ${username}?`
    }))) return;

    setSendingPush(userId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/push-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          userId,
          title: 'SoArtistic Test',
          message: select({ es: 'Esta es una notificación de prueba 🚀', en: 'This is a test notification 🚀' }),
          data: { type: 'test' }
        })
      });

      const result = await response.json();
      
      if (!response.ok) throw new Error(result.error || 'Failed to send');
      
      alert(select({ es: 'Notificación enviada', en: 'Notification sent' }));
    } catch (err) {
      const message = err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' });
      alert(select({ es: `Error: ${message}`, en: `Error: ${message}` }));
    } finally {
      setSendingPush(null);
    }
  };

  useEffect(() => {
    if (isAdmin || isModerator) {
      loadUsers();
    }
  }, [roleFilter, statusFilter, isAdmin, isModerator]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      let query = supabase
        .from('profiles')
        .select(`
          id,
          username,
          full_name,
          user_type,
          is_premium,
          avatar_url,
          location,
          created_at,
          account_status,
          suspension_reason,
          suspended_until
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (roleFilter !== 'all') {
        query = query.eq('user_type', roleFilter);
      }
      
      if (statusFilter !== 'all') {
        query = query.eq('account_status', statusFilter);
      }

      const { data, error: usersError } = await query;
      if (usersError) throw usersError;

      setUsers(data || []);
    } catch (err) {
      setError(select({ es: 'Error al cargar usuarios', en: 'Failed to load users' }));
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser || !isAdmin) return;

    setSaving(true);
    try {
      const updates: Record<string, unknown> = {
        user_type: newRole,
        account_status: newStatus,
        updated_at: new Date().toISOString(),
      };

      // Handle suspension
      if (newStatus === 'suspended') {
        const suspensionDate = new Date();
        suspensionDate.setDate(suspensionDate.getDate() + suspensionDays);
        updates.suspended_until = suspensionDate.toISOString();
        updates.suspension_reason = suspensionReason;
        updates.moderated_by = user.id;
      } else {
        updates.suspended_until = null;
        updates.suspension_reason = null;
        if (newStatus === 'active') {
          updates.moderated_by = null;
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', editingUser.id);

      if (error) throw error;

      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === editingUser.id 
          ? { ...u, ...updates }
          : u
      ));

      // Create system notification for the user
      if (newRole !== editingUser.user_type) {
        await supabase
          .from('notifications')
          .insert({
            user_id: editingUser.id,
            type: 'system',
            title: select({ es: 'Cambio de rol', en: 'Role updated' }),
            message: select({
              es: `Tu rol ha sido actualizado a ${getRoleLabel(newRole)}`,
              en: `Your role has been updated to ${getRoleLabel(newRole)}`,
            }),
            metadata: {
              old_role: editingUser.user_type,
              new_role: newRole,
              updated_by: user.username
            }
          });
      }

      if (newStatus !== 'active' && newStatus !== editingUser.account_status) {
        const statusActionEs =
          newStatus === 'suspended'
            ? 'suspendida'
            : newStatus === 'warned'
            ? 'advertida'
            : 'baneada';
        const statusActionEn =
          newStatus === 'suspended'
            ? 'suspended'
            : newStatus === 'warned'
            ? 'warned'
            : 'banned';
        await supabase
          .from('notifications')
          .insert({
            user_id: editingUser.id,
            type: 'system',
            title: select({ es: 'Estado de cuenta actualizado', en: 'Account status updated' }),
            message: select({
              es: `Tu cuenta ha sido ${statusActionEs}${suspensionReason ? `: ${suspensionReason}` : ''}`,
              en: `Your account has been ${statusActionEn}${suspensionReason ? `: ${suspensionReason}` : ''}`,
            }),
            metadata: {
              old_status: editingUser.account_status,
              new_status: newStatus,
              reason: suspensionReason,
              suspended_until: updates.suspended_until,
              updated_by: user.username
            }
          });
      }

      setEditingUser(null);
      setNewRole('visitor');
      setNewStatus('active');
      setSuspensionReason('');
      setSuspensionDays(7);
    } catch (err) {
      setError(
        `${select({ es: 'Error al actualizar usuario', en: 'Failed to update user' })}: ${(err as Error).message}`
      );
      console.error('Error updating user:', err);
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role: string) => {
    const labels = {
      visitor: select({ es: 'Visitante', en: 'Visitor' }),
      artist: select({ es: 'Artista', en: 'Artist' }),
      moderator: select({ es: 'Moderador', en: 'Moderator' }),
      admin: select({ es: 'Administrador', en: 'Administrator' })
    };
    return labels[role as keyof typeof labels] || role;
  };

  const getRoleColor = (role: string) => {
    const colors = {
      visitor: 'bg-gray-100 text-gray-800',
      artist: 'bg-purple-100 text-purple-800',
      moderator: 'bg-blue-100 text-blue-800',
      admin: 'bg-red-100 text-red-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      warned: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-orange-100 text-orange-800',
      banned: 'bg-red-100 text-red-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status?: string) => {
    const labels = {
      active: select({ es: 'Activo', en: 'Active' }),
      warned: select({ es: 'Advertido', en: 'Warned' }),
      suspended: select({ es: 'Suspendido', en: 'Suspended' }),
      banned: select({ es: 'Baneado', en: 'Banned' })
    };
    const key = (status || 'active') as keyof typeof labels;
    return labels[key] || labels.active;
  };

  const filteredUsers = users.filter(u => {
    if (searchQuery) {
      return (
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return true;
  });

  const userStats = {
    total: users.length,
    artists: users.filter(u => u.user_type === 'artist').length,
    visitors: users.filter(u => u.user_type === 'visitor').length,
    moderators: users.filter(u => u.user_type === 'moderator').length,
    admins: users.filter(u => u.user_type === 'admin').length,
    suspended: users.filter(u => u.account_status === 'suspended').length,
    banned: users.filter(u => u.account_status === 'banned').length,
  };

  if (!isAdmin && !isModerator) {
    return (
      <div className="p-6 text-center">
        <Shield size={48} className="mx-auto text-red-300 mb-4" />
        <h3 className="text-lg font-medium text-red-900 mb-2">
          {select({ es: 'Acceso denegado', en: 'Access denied' })}
        </h3>
        <p className="text-red-700">
          {select({
            es: 'Solo administradores y moderadores pueden acceder a esta sección.',
            en: 'Only administrators and moderators can access this section.',
          })}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 p-6">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-gray-200 h-20 rounded-lg"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="text-blue-600" size={20} />
          {select({ es: 'Gestión de usuarios', en: 'User management' })}
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-900">{userStats.total}</div>
            <div className="text-sm text-blue-700">{select({ es: 'Total de usuarios', en: 'Total users' })}</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-900">{userStats.artists}</div>
            <div className="text-sm text-purple-700">{select({ es: 'Artistas', en: 'Artists' })}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{userStats.visitors}</div>
            <div className="text-sm text-gray-700">{select({ es: 'Visitantes', en: 'Visitors' })}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-900">{userStats.suspended + userStats.banned}</div>
            <div className="text-sm text-red-700">{select({ es: 'Suspendidos/Baneados', en: 'Suspended/Banned' })}</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={select({
                  es: 'Buscar por nombre, usuario o email...',
                  en: 'Search by name, username, or email...',
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as 'all' | 'artist' | 'visitor' | 'moderator' | 'admin')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{select({ es: 'Todos los roles', en: 'All roles' })}</option>
              <option value="visitor">{select({ es: 'Visitantes', en: 'Visitors' })}</option>
              <option value="artist">{select({ es: 'Artistas', en: 'Artists' })}</option>
              <option value="moderator">{select({ es: 'Moderadores', en: 'Moderators' })}</option>
              <option value="admin">{select({ es: 'Administradores', en: 'Administrators' })}</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'warned' | 'suspended' | 'banned')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{select({ es: 'Todos los estados', en: 'All statuses' })}</option>
              <option value="active">{select({ es: 'Activos', en: 'Active' })}</option>
              <option value="warned">{select({ es: 'Advertidos', en: 'Warned' })}</option>
              <option value="suspended">{select({ es: 'Suspendidos', en: 'Suspended' })}</option>
              <option value="banned">{select({ es: 'Baneados', en: 'Banned' })}</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{select({ es: 'Usuario', en: 'User' })}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{select({ es: 'Rol', en: 'Role' })}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{select({ es: 'Estado', en: 'Status' })}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{select({ es: 'Registro', en: 'Joined' })}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{select({ es: 'Acciones', en: 'Actions' })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((userData) => (
                  <tr key={userData.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={getFullUrl(userData.avatar_url) || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                          alt={userData.full_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{userData.full_name}</div>
                          <div className="text-sm text-gray-500">@{userData.username}</div>
                          {userData.location && (
                            <div className="text-xs text-gray-400">{userData.location}</div>
                          )}
                        </div>
                        {userData.is_premium && (
                          <span className="text-yellow-500 text-sm">✨</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${getRoleColor(userData.user_type)}`}>
                        {getRoleLabel(userData.user_type)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(userData.account_status || 'active')}`}>
                        {getStatusLabel(userData.account_status)}
                      </span>
                      {userData.suspended_until && new Date(userData.suspended_until) > new Date() && (
                        <div className="text-xs text-orange-600 mt-1">
                          {select({ es: 'Hasta', en: 'Until' })}: {new Date(userData.suspended_until).toLocaleDateString(locale)}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-gray-900">
                        {new Date(userData.created_at).toLocaleDateString(locale)}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => sendTestPush(userData.id, userData.username)}
                          disabled={sendingPush === userData.id}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title={select({ es: 'Enviar notificación push de prueba', en: 'Send test push notification' })}
                        >
                          <div className="relative">
                            <span className="text-lg">🔔</span>
                            {sendingPush === userData.id && (
                              <span className="absolute -top-1 -right-1 w-3 h-3 border-2 border-white border-t-purple-600 rounded-full animate-spin"></span>
                            )}
                          </div>
                        </button>
                        {isAdmin && userData.id !== user?.id && (
                          <button
                            onClick={() => {
                              setEditingUser(userData);
                              setNewRole(userData.user_type);
                              setNewStatus((userData.account_status as 'active' | 'warned' | 'suspended' | 'banned') || 'active');
                              setSuspensionReason(userData.suspension_reason || '');
                            }}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                          >
                            <Edit2 size={14} />
                            {select({ es: 'Editar', en: 'Edit' })}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {select({ es: 'No se encontraron usuarios', en: 'No users found' })}
              </h3>
              <p className="text-gray-500">
                {select({ es: 'Intenta con otros filtros de búsqueda', en: 'Try different filter options' })}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {select({ es: 'Editar usuario', en: 'Edit user' })}
                </h2>
                <button
                  onClick={() => setEditingUser(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-6">
                {/* User Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src={editingUser.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                      alt={editingUser.full_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{editingUser.full_name}</div>
                      <div className="text-sm text-gray-500">@{editingUser.username}</div>
                    </div>
                  </div>
                </div>

                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {select({ es: 'Rol del usuario', en: 'User role' })}
                  </label>
                  <div className="space-y-2">
                    {['visitor', 'artist', 'moderator', 'admin'].map((role) => (
                      <label key={role} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="role"
                          value={role}
                          checked={newRole === role}
                          onChange={(e) => setNewRole(e.target.value as 'artist' | 'visitor' | 'moderator' | 'admin')}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-2">
                          {role === 'admin' && <Crown size={16} className="text-red-600" />}
                          {role === 'moderator' && <Shield size={16} className="text-blue-600" />}
                          {role === 'artist' && <span className="text-purple-600">🎨</span>}
                          {role === 'visitor' && <UserCheck size={16} className="text-gray-600" />}
                          <span className="font-medium">{getRoleLabel(role)}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Account Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {select({ es: 'Estado de la cuenta', en: 'Account status' })}
                  </label>
                  <div className="space-y-2">
                    {['active', 'warned', 'suspended', 'banned'].map((status) => (
                      <label key={status} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="status"
                          value={status}
                          checked={newStatus === status}
                          onChange={(e) => setNewStatus(e.target.value as 'active' | 'warned' | 'suspended' | 'banned')}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-2">
                          {status === 'active' && <div className="w-2 h-2 bg-green-500 rounded-full" />}
                          {status === 'warned' && <AlertTriangle size={16} className="text-yellow-600" />}
                          {status === 'suspended' && <div className="w-2 h-2 bg-orange-500 rounded-full" />}
                          {status === 'banned' && <div className="w-2 h-2 bg-red-500 rounded-full" />}
                          <span className="font-medium capitalize">{getStatusLabel(status)}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Suspension Details */}
                {newStatus === 'suspended' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-4">
                    <h4 className="font-medium text-orange-900">
                      {select({ es: 'Detalles de suspensión', en: 'Suspension details' })}
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Duración (días)', en: 'Duration (days)' })}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={suspensionDays}
                        onChange={(e) => setSuspensionDays(parseInt(e.target.value) || 7)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Razón de suspensión', en: 'Suspension reason' })}
                      </label>
                      <textarea
                        rows={3}
                        value={suspensionReason}
                        onChange={(e) => setSuspensionReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                        placeholder={select({
                          es: 'Explica el motivo de la suspensión...',
                          en: 'Explain the reason for the suspension...',
                        })}
                      />
                    </div>
                  </div>
                )}

                {/* Warning for role changes */}
                {newRole !== editingUser.user_type && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="text-yellow-600" size={16} />
                      <span className="text-sm text-yellow-700 font-medium">
                        {select({
                          es: `Cambiarás el rol de "${getRoleLabel(editingUser.user_type)}" a "${getRoleLabel(newRole)}"`,
                          en: `You will change the role from "${getRoleLabel(editingUser.user_type)}" to "${getRoleLabel(newRole)}"`,
                        })}
                      </span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setEditingUser(null)}
                    disabled={saving}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {select({ es: 'Cancelar', en: 'Cancel' })}
                  </button>
                  <button
                    onClick={handleUpdateUser}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        {select({ es: 'Guardando...', en: 'Saving...' })}
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {select({ es: 'Guardar cambios', en: 'Save changes' })}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementView;
